/**
 * @desc The cache-tag convention shared by every layer that caches insights data: Next's data cache
 * in `apps/insights` and the backend's Valkey cache. Both tag with the same strings, so one event
 * (an ingest, a Sanity publish) invalidates both layers by name.
 *
 * - `dataset:<name>` — data derived from one ingested dataset. `<name>` is the ClickHouse table the
 *   ingest writes (`annual_ggr`, `fx`; Lane H2), because that is what both an ingest and a reader
 *   know without consulting the catalogue. Validated by charset, not against a registry.
 * - `type:<docType>` — content of one Sanity document type (`operator`).
 * - `market:<slug>`, `group:<slug>` — data scoped to one market or operator group.
 *
 * Dependency-free: imported by the insights proxy-side bundle as well as the backend.
 */
export type CacheTagKind = 'dataset' | 'type' | 'market' | 'group';

export type ParsedCacheTag = { kind: CacheTagKind; value: string };

/** @desc Letters, digits and underscores, starting with a letter — covers dataset keys and API names. */
const DATASET_NAME = /^[A-Za-z][A-Za-z0-9_]{0,63}$/;
/** @desc Sanity type names: letters, digits, `_`, `-` and `.` (`sanity.imageAsset`), not leading a digit. */
const DOC_TYPE = /^[A-Za-z_][A-Za-z0-9_.-]{0,63}$/;
/** @desc Lower-case URL slugs. */
const SLUG = /^[a-z0-9-]{1,64}$/;

const VALUE_PATTERNS: Record<CacheTagKind, RegExp> = {
  dataset: DATASET_NAME,
  type: DOC_TYPE,
  market: SLUG,
  group: SLUG,
};

export const CACHE_TAG_KINDS = Object.keys(VALUE_PATTERNS) as readonly CacheTagKind[];

function isKind(kind: string): kind is CacheTagKind {
  return Object.prototype.hasOwnProperty.call(VALUE_PATTERNS, kind);
}

export function isValidCacheTagValue(kind: CacheTagKind, value: string): boolean {
  return VALUE_PATTERNS[kind].test(value);
}

/** @desc The tag for `kind` and `value`; throws on a value outside the kind's charset. */
export function cacheTag(kind: CacheTagKind, value: string): string {
  if (!isValidCacheTagValue(kind, value)) {
    throw new Error(`Invalid ${kind} cache-tag value: ${JSON.stringify(value)}`);
  }
  return `${kind}:${value}`;
}

export const CacheTags = {
  dataset: (name: string) => cacheTag('dataset', name),
  type: (docType: string) => cacheTag('type', docType),
  market: (slug: string) => cacheTag('market', slug),
  group: (slug: string) => cacheTag('group', slug),
} as const;

/** @desc Parses a tag string; `null` for an unknown kind or a value outside the kind's charset. */
export function parseCacheTag(tag: string): ParsedCacheTag | null {
  const separator = tag.indexOf(':');
  if (separator === -1) return null;
  const kind = tag.slice(0, separator);
  const value = tag.slice(separator + 1);
  if (!isKind(kind) || !isValidCacheTagValue(kind, value)) return null;
  return { kind, value };
}

/**
 * @desc Response header on which an insights fragment lists the tags it was rendered from
 * (comma-separated), so a caller can see what will invalidate it. Here, not in the fragments
 * contract, so the insights proxy can name it without importing Zod.
 */
export const CACHE_TAGS_HEADER = 'x-insights-cache-tags';

export function isCacheTag(tag: string): boolean {
  return parseCacheTag(tag) !== null;
}
